/**
 * Baixar, imprimir e compartilhar arquivos — web e app.
 *
 * POR QUE EXISTE. Dentro do WKWebView (iOS) e do WebView (Android) o app nao
 * tem "pasta de downloads" nem janela nova:
 *
 *   - `window.open(...)` devolve **null**. Todo botao de imprimir fazia
 *     `win.document.write(...)` em cima desse null, estourava um TypeError no
 *     handler do clique e o toque simplesmente nao fazia nada.
 *   - `<a download>` com blob: URL e ignorado. E o caminho que o `jsPDF.save()`
 *     e o `XLSX.writeFile()` usam por dentro — dai "exportar PDF" tambem morrer
 *     em silencio.
 *
 * O equivalente nativo de "baixar" e a FOLHA DE COMPARTILHAMENTO: o arquivo vai
 * para a pasta de cache do app e o sistema abre as opcoes — Arquivos, WhatsApp,
 * Mail e, para PDF, a propria impressao (AirPrint no iOS, serviço de impressao
 * no Android). Ou seja, imprimir continua possivel, por um caminho que existe
 * no celular.
 *
 * Na web nada muda: continua o download de sempre e a janela de impressao.
 */

import { isNative, getPlugin } from './native';
import { captureMessage } from '../pwa/sentry';

const DIRETORIO = 'CACHE'; // temporario: o sistema limpa sozinho

/** Tipo MIME das planilhas — o XLSX.write devolve bytes crus, sem tipo. */
export const MIME_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';


/** Extrai so o conteudo base64 de um dataURL (`data:...;base64,XXXX`). */
function apenasBase64(dataUrl) {
  const i = String(dataUrl).indexOf(',');
  return i >= 0 ? String(dataUrl).slice(i + 1) : String(dataUrl);
}

function blobParaBase64(blob) {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onerror = () => reject(new Error('falha ao ler o arquivo'));
    leitor.onload = () => resolve(apenasBase64(leitor.result));
    leitor.readAsDataURL(blob);
  });
}

/** Baixa pelo navegador — comportamento historico da web. */
function baixarNaWeb(blob, nome) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revogar na hora corta o download no Firefox; um tick e suficiente.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Entrega um arquivo para a pessoa: download na web, folha de compartilhamento
 * no app.
 *
 * @param {Blob} blob conteudo
 * @param {string} nome nome do arquivo COM extensao (vira o titulo no share)
 * @returns {Promise<boolean>} false se nao deu para entregar (quem chama avisa)
 */
export async function entregarArquivo(blob, nome) {
  if (!isNative()) {
    baixarNaWeb(blob, nome);
    return true;
  }

  const Filesystem = getPlugin('Filesystem');
  const Share = getPlugin('Share');
  if (!Filesystem || !Share) return false;

  try {
    const base64 = await blobParaBase64(blob);
    // `recursive` para o caso de o cache ainda nao existir no primeiro uso.
    await Filesystem.writeFile({ path: nome, data: base64, directory: DIRETORIO, recursive: true });
    const { uri } = await Filesystem.getUri({ path: nome, directory: DIRETORIO });
    await Share.share({ title: nome, files: [uri] });
    return true;
  } catch (e) {
    // Fechar a folha de compartilhamento REJEITA a promise. Nao e erro: a
    // pessoa desistiu. Sem este ramo viraria "unhandled rejection" e, com o
    // Sentry ligado, um alerta falso a cada desistencia.
    const motivo = ((e && (e.message || e.errorMessage)) || '').toLowerCase();
    if (motivo.includes('cancel') || motivo.includes('abort') || motivo.includes('dismiss')) return true;
    captureMessage('arquivo-nao-entregue', 'error', { nome, detalhe: String(e && (e.message || e)) });
    return false;
  }
}

/** true quando da para entregar arquivo nesta plataforma. */
export function podeEntregarArquivo() {
  return !isNative() || (!!getPlugin('Filesystem') && !!getPlugin('Share'));
}

/**
 * Imprime um recibo.
 *
 * Web: janela nova com o HTML + `print()`, exatamente como sempre foi — e o
 * que sai na impressora termica todo dia.
 *
 * App: nao existe janela nova, entao o recibo e DESENHADO em PDF a partir de
 * `blocos` e vai para a folha de compartilhamento (imprimir, salvar, WhatsApp).
 *
 * POR QUE OS DOIS CAMINHOS. A primeira tentativa converteu o proprio HTML em
 * PDF com `jsPDF.html()` (html2canvas por baixo). No WKWebView o arquivo saiu
 * em branco: o html2canvas rasteriza o que esta visivel na tela, e o elemento
 * ficava fora dela. Desenhar com as primitivas do jsPDF e o caminho que ja
 * funcionava no mesmo aparelho (inventario, extrato) — ver platform/recibo.js.
 *
 * O preco e a duplicacao: cada tela monta o HTML e a lista de blocos. Elas
 * ficam LADO A LADO na mesma funcao de proposito, para quem mexer numa ver a
 * outra. Unificar exigiria gerar o HTML a partir dos blocos, o que mudaria a
 * impressao da web — que funciona e ninguem pediu para mexer.
 *
 * @param {{ html: string, blocos: Array, nome?: string, larguraMm?: number }} recibo
 * @returns {Promise<boolean>} false quando nao foi possivel
 */
export async function imprimirRecibo({ html, blocos, nome = 'documento.pdf', larguraMm = 210 }) {
  if (!isNative()) {
    const janela = window.open('', '_blank', `width=${larguraMm <= 100 ? 350 : 800},height=600`);
    if (!janela) return false;
    janela.document.write(html);
    janela.document.close();
    // O print imediato pega a pagina antes das fontes e do layout assentarem.
    setTimeout(() => janela.print(), 300);
    return true;
  }

  if (!podeEntregarArquivo()) return false;

  try {
    const { pdfDeRecibo } = await import('./recibo');
    const doc = await pdfDeRecibo(blocos, { larguraMm });
    return await entregarArquivo(doc.output('blob'), nome);
  } catch (e) {
    // Quem chama nao trata a promessa: se estourasse aqui, o toque voltaria a
    // "nao fazer nada" — exatamente o defeito que este modulo existe para tirar.
    captureMessage('recibo-falhou', 'error', { nome, detalhe: String(e && (e.message || e)) });
    return false;
  }
}

/** Atalho para os relatorios que ja montam um documento jsPDF. */
export function entregarPdf(doc, nome) {
  return entregarArquivo(doc.output('blob'), nome);
}
